import React, { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '@/context/ThemeContext'
import { useAuth } from '@/features/auth/hooks/useAuth'

// Lands here after /login redirects to Microsoft and Microsoft's own redirect
// bounces back through the API's /auth/microsoft/callback, which hands this
// page a one-time code. Mirrors GoogleCallbackPage.tsx. This page's only job
// is to trade that code for a session and continue on; errors go back to
// /login so there's one sign-in form, not two.
const MicrosoftCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { currentTheme } = useTheme()
  const { loginWithMicrosoftCode } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const ranRef = useRef(false)

  useEffect(() => {
    // StrictMode double-invokes effects in dev; the code is single-use, so a
    // second exchange would just fail — guard against running it twice.
    if (ranRef.current) return
    ranRef.current = true

    const code = searchParams.get('code')
    if (!code) {
      navigate('/login?microsoftAuth=error&reason=missing_params', { replace: true })
      return
    }

    loginWithMicrosoftCode(code).catch((err) => {
      // The session exchange (POST /auth/microsoft/session) throws an
      // AppError whose message is already written for the person signing in
      // (see auth/microsoft/service.ts), unlike the callback redirect's
      // `reason` code below — so it's passed through as-is rather than
      // mapped through microsoftSignInErrorCopy.
      const message = err instanceof Error ? err.message : 'Something went wrong signing in with Microsoft. Please try again.'
      setError(message)
      navigate('/login', { replace: true, state: { microsoftSignInError: message } })
    })
  }, [searchParams, navigate, loginWithMicrosoftCode])

  return (
    <div
      className="flex min-h-screen items-center justify-center transition-colors duration-300"
      style={{ backgroundColor: currentTheme.primary }}
    >
      {!error && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: currentTheme.text }} />
          <p className="text-sm" style={{ color: currentTheme.secondrytext }}>
            Finishing sign-in with Microsoft…
          </p>
        </div>
      )}
    </div>
  )
}

export default MicrosoftCallbackPage
